import BackButton from "./BackButton";
import { STARTING_RATING } from "../utils/elo";
import { ArrowDownWideNarrow, ArrowUpNarrowWide } from "lucide-react";

import {
    useState,
} from "react";

import {
    loadRatingHistory,
} from "../utils/ratingHistory";

export default function StatsScreen({
    ratings,
    categories,
    onBack,
}) {
    const [ratingHistory] = useState(
        () => loadRatingHistory()
    );

    const [sortOrder, setSortOrder] = useState("desc");

    const [timeRange, setTimeRange] = useState("30D");

    const TIME_RANGES = {
        "7D": 7,
        "30D": 30,
        "90D": 90,
        "1Y": 365,
        ALL: null,
    };

    const overallRating = Math.round(
        ratings?.overall ?? STARTING_RATING
    );

    const baselineSnapshot =
        getBaselineSnapshot(
            ratingHistory,
            timeRange
        );

    const overallRatingChange =
        baselineSnapshot
            ? ratings.overall -
            baselineSnapshot.o
            : 0;

    const categoryRatings = [...categories]
        .map((category) => {
            const categoryId =
                String(category.id);

            const categoryData =
                ratings?.categories?.[
                categoryId
                ];

            const currentRating =
                categoryData?.rating ?? null;

            const previousRating =
                baselineSnapshot
                    ?.c?.[categoryId]?.r ??
                STARTING_RATING;

            return {
                ...category,

                rating:
                    categoryData?.rating ?? null,

                attempts:
                    categoryData?.attempts ?? 0,

                ratingChange:
                    currentRating == null
                        ? null
                        : currentRating -
                        previousRating,
            };
        })
        .sort((a, b) => {
            // Put unrated categories at the bottom
            if (a.rating == null && b.rating == null) {
                return a.name.localeCompare(b.name);
            }

            if (a.rating == null) return 1;
            if (b.rating == null) return -1;

            if (sortOrder === "asc") {
                return a.rating - b.rating;
            }

            return b.rating - a.rating;
        });

    function getBaselineSnapshot(
        history,
        timeRange
    ) {
        if (history.length === 0) {
            return null;
        }

        const days = TIME_RANGES[timeRange];

        if (days == null) {
            return history[0];
        }

        const cutoff =
            Date.now() -
            days * 24 * 60 * 60 * 1000;

        // Find the most recent snapshot
        // from before the cutoff.
        const beforeCutoff = [...history]
            .reverse()
            .find(
                (snapshot) =>
                    snapshot.t <= cutoff
            );

        // If the app doesn't have that much
        // history yet, use the oldest snapshot.
        return beforeCutoff ?? history[0];
    }

    function formatRatingChange(change) {
        const rounded = Math.round(change);

        if (rounded > 0) {
            return `+${rounded}`;
        }

        if (rounded < 0) {
            return `${rounded}`;
        }

        return "–";
    }

    function getRatingChangeClass(change) {
        const rounded = Math.round(change);

        if (rounded > 0) {
            return "rating-change--positive";
        }

        if (rounded < 0) {
            return "rating-change--negative";
        }

        return "rating-change--neutral";
    }

    return (
        <div className="page-screen">
            <div className="page-content">
                <div className="page-toolbar">
                    <BackButton
                        onClick={onBack}
                    />
                </div>

                <header className="page-header">
                    <h1>Stats</h1>
                </header>

                <div className="stats-time-ranges">
                    {Object.keys(TIME_RANGES).map(
                        (range) => (
                            <button
                                key={range}
                                type="button"
                                className={
                                    timeRange === range
                                        ? "stats-time-range stats-time-range--active"
                                        : "stats-time-range"
                                }
                                onClick={() =>
                                    setTimeRange(range)
                                }
                            >
                                {range}
                            </button>
                        )
                    )}
                </div>

                <section className="stats-overall">
                    <span className="stats-overall__label">
                        Overall Rating
                    </span>

                    <strong className="stats-overall__rating">
                        {overallRating}
                    </strong>

                    <span
                        className={`stats-overall__change ${getRatingChangeClass(
                            overallRatingChange
                        )}`}
                    >
                        {formatRatingChange(overallRatingChange)}
                    </span>
                </section>

                <section className="stats-categories">
                    <div className="stats-categories__header">
                        <h2>Category Ratings</h2>

                        <button
                            type="button"
                            className="stats-sort-button"
                            onClick={() =>
                                setSortOrder((current) =>
                                    current === "desc" ? "asc" : "desc"
                                )
                            }
                        >
                            {sortOrder === "desc" ? (
                                <ArrowDownWideNarrow size={17} />
                            ) : (
                                <ArrowUpNarrowWide size={17} />
                            )}
                        </button>
                    </div>

                    <div className="stats-categories__grid">
                        {categoryRatings.map((category) => (
                            <div
                                key={category.id}
                                className="stats-category"
                            >
                                <div>
                                    <div className="stats-category__name">
                                        {category.name}
                                    </div>

                                    {category.attempts > 0 &&
                                        category.attempts < 30 && (
                                            <div className="stats-category__provisional">
                                                Provisional · {category.attempts} clue
                                                {category.attempts === 1 ? "" : "s"}
                                            </div>
                                        )}
                                </div>

                                <div className="stats-category__rating-group">
                                    <strong className="stats-category__rating">
                                        {category.rating == null
                                            ? "—"
                                            : Math.round(category.rating)}
                                    </strong>

                                    {category.ratingChange != null && (
                                        <span
                                            className={`stats-category__change ${getRatingChangeClass(
                                                category.ratingChange
                                            )
                                                }`}
                                        >
                                            {formatRatingChange(
                                                category.ratingChange
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div >
    );
}